import { Box, Divider, List } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import LocaleKeys from '../../../locales/keys';
import { makeStyles } from '@material-ui/core/styles';
import { getCurrencyDetails, formatDateString } from '../../../utils/common';
import OrderHistoryListHeader from './OrderHistoryListHeader.jsx';
import ListItemComponent from '../../../common/listItem/ListItemComponent.jsx';
import { getThumbnailUrl } from '../../../checkout/helpers/paymentOrderHelpers';

const useStyles = makeStyles((theme) => ({
  dividerBlack: {
    backgroundColor: `${theme.palette.custom.lightBlack}`,
  },
  orderList: {
    backgroundColor: theme.palette.custom.white,
    borderRadius: '10px',
    border: `solid 1px ${theme.palette.custom.lightBlack}`,
    marginBottom: '20px',
    padding: '20px',
  },
  orderListItem: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      padding: '0 15px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  orderListItemSmallDevice: {
    paddingTop: '20px',
    paddingBottom: '3px',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  orderListItemTotals: {
    paddingTop: '20px',
    paddingBottom: '3px',
  },
  orderListItemWidth: {
    margin: 0,
    maxWidth: '283px',
  },
  affiliateLogo: {
    width: '100px',
    height: '48px',
    marginTop: '10px',
  },
  productLogo: {
    width: 'auto',
    height: 'auto',
    marginBottom: '15px',
    marginTop: '10px',
    maxWidth: '220px',
  },
  planName: {
    [theme.breakpoints.up('sm')]: {
      maxWidth: '21.5rem',
    },
  },
  subTotal: {
    textAlign: 'end',
  },
  totalAmount: {
    [theme.breakpoints.up('md')]: {
      fontSize: '25px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px',
    },
  },
  orderListItemMargin: {
    margin: 0,
  },
  orderListHeader: {
    padding: 0,
  },
}));

const OrderHistoryType = ({ searchOrders, productMetadata }) => {
  const {
    Completed,
    Currency,
    OrderItems,
    OrderNumber,
    Ordered,
    OrderStatusName,
    Totals,
    TypeName,
  } = searchOrders;
  const { NetTotalAmount, SubTotalAmount, TaxAmount } = Totals;
  const classes = useStyles();
  const { t } = useTranslation();

  return OrderItems.map((orderItem, index) => (
    <Box className={classes.orderList} key={index}>
      <List className={classes.orderListItem}>
        {orderItem.PricingPlan.SubscriptionBillingCycleName.toLowerCase().includes(
          'affiliateplan',
        ) ? (
          <OrderHistoryListHeader
            productThumbnailUrl={getThumbnailUrl(
              orderItem.Product,
              orderItem.PricingPlan,
              productMetadata,
            )}
            pricingPlanUrl={orderItem.PricingPlan.ImageUrl}
            classname={classes}
          />
        ) : (
          <OrderHistoryListHeader
            productThumbnailUrl={getThumbnailUrl(
              orderItem.Product,
              orderItem.PricingPlan,
              productMetadata,
            )}
            classname={classes}
          />
        )}

        <ListItemComponent
          classNameListItem={classes.orderListItemSmallDevice}
          label={[
            {
              label: t(LocaleKeys.orderHistory.orderType),
              variant: 'h5',
              classname: classes.orderListItemWidth,
            },
            {
              label: orderItem.PricingPlan.Name,
              variant: 'h5',
              classname: classes.planName,
            },
          ]}
        />

        <Divider className={classes.dividerBlack} />

        <ListItemComponent
          classNameListItem={classes.orderListItemSmallDevice}
          label={[
            {
              label: t(LocaleKeys.orderHistory.orderDate),
              variant: 'body1',
              classname: classes.orderListItemWidth,
            },
            {
              label: formatDateString(Completed || Ordered),
              variant: 'h5',
              classname: classes.orderListItemMargin,
            },
          ]}
        />
        <Divider />

        <ListItemComponent
          classNameListItem={classes.orderListItemSmallDevice}
          label={[
            {
              label: t(LocaleKeys.orderHistory.orderNumber),
              variant: 'body1',
              classname: classes.orderListItemWidth,
            },
            {
              label: OrderNumber,
              variant: 'h5',
              classname: classes.orderListItemMargin,
            },
          ]}
        />
        <Divider />

        <ListItemComponent
          classNameListItem={classes.orderListItemSmallDevice}
          label={[
            {
              label: t(LocaleKeys.orderHistory.type),
              variant: 'body1',
              classname: classes.orderListItemWidth,
            },
            {
              label: TypeName,
              variant: 'h5',
              classname: classes.orderListItemMargin,
            },
          ]}
        />
        <Divider />

        <ListItemComponent
          classNameListItem={classes.orderListItemSmallDevice}
          label={[
            {
              label: t(LocaleKeys.orderHistory.status),
              variant: 'body1',
              classname: classes.orderListItemWidth,
            },
            {
              label: OrderStatusName,
              variant: 'h5',
              classname: classes.orderListItemMargin,
            },
          ]}
        />
        <Divider />

        <ListItemComponent
          classNameListItem={classes.orderListItemTotals}
          label={[
            {
              label: t(LocaleKeys.orderHistory.subTotal),
              variant: 'body1',
              classname: classes.orderListItemMargin,
            },
            {
              label: getCurrencyDetails(SubTotalAmount, Currency)
                .priceWithCurrencySymbol,
              variant: 'h5',
              typographyClass: classes.subTotal,
              classname: classes.orderListItemMargin,
            },
          ]}
        />
        <Divider />

        <ListItemComponent
          classNameListItem={classes.orderListItemTotals}
          label={[
            {
              label: t(LocaleKeys.orderHistory.tax),
              variant: 'body1',
              classname: classes.orderListItemMargin,
            },
            {
              label: getCurrencyDetails(TaxAmount, Currency)
                .priceWithCurrencySymbol,
              variant: 'h5',
              typographyClass: classes.subTotal,
              classname: classes.orderListItemMargin,
            },
          ]}
        />
        <Divider className={classes.dividerBlack} />

        <ListItemComponent
          classNameListItem={classes.orderListItemTotals}
          label={[
            { label: t(LocaleKeys.orderHistory.orderTotal), variant: 'h5' },
            {
              label: getCurrencyDetails(NetTotalAmount, Currency)
                .priceWithCurrencySymbol,
              variant: 'h2',
              typographyClass: `${classes.totalAmount} ${classes.subTotal}`,
            },
          ]}
        />
      </List>
    </Box>
  ));
};

OrderHistoryType.propTypes = {
  searchOrders: PropTypes.object.isRequired,
};

export default OrderHistoryType;
